import React from 'react';
import { Box, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography, useTheme } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { KycDetails, KycAddressDetails } from '../../redux-store/types/api-types';

export type KycDataWithCheckboxes = KycDetails & {
    isNameChecked: boolean;
    isEmailChecked: boolean;
    isMobileChecked: boolean;
    isAddressChecked: boolean;
} | null;

export interface KycEditPopupProps {
    open: boolean;
    kycData: KycDataWithCheckboxes;
    setKycData: React.Dispatch<React.SetStateAction<KycDataWithCheckboxes>>;
    onClose: () => void;
    onSubmit: () => void;
    isSubmitBtnLoading?: boolean;
    isCloseBtnLoading?: boolean;
}

const iconMap: Record<string, React.ReactNode> = {
    person: <PersonOutlineIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
    email: <EmailOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
    phone: <PhoneAndroidOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
    address: <LocationOnOutlinedIcon sx={{ fontSize: 28, color: 'primary.main' }} />,
};

const addressFieldConfig: { label: string; key: keyof KycAddressDetails }[] = [
    { label: 'Address Type', key: 'address_type' },
    { label: 'Address 1', key: 'address1' },
    { label: 'Address 2', key: 'address2' },
    { label: 'Address 3', key: 'address3' },
    { label: 'City', key: 'city' },
    { label: 'State', key: 'state' },
    { label: 'Country', key: 'country' },
    { label: 'Pincode', key: 'pincode' },
];

export default function KycEditPopup({
    open,
    kycData,
    setKycData,
    onClose,
    onSubmit,
    isSubmitBtnLoading = false,
    isCloseBtnLoading = false,
}: KycEditPopupProps): JSX.Element {
    const theme = useTheme();

    const handleCheckboxChange = (field: 'isNameChecked' | 'isEmailChecked' | 'isMobileChecked' | 'isAddressChecked') => {
        if (!kycData) return;

        setKycData({
            ...kycData,
            [field]: !kycData[field],
        });
    };

    return (
        <Dialog
            open={open}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    p: 0,
                    maxWidth: 680,
                    overflowY: 'auto',
                    maxHeight: '90vh',
                },
            }}
        >
            <DialogTitle sx={{ p: 3, pb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h2">
                            Edit KYC Data
                        </Typography>
                        <Typography variant="body_500" color="text.secondary" sx={{ mt: 0.5 }}>
                            Current KYC data is shown below each field
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: 3, py: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box
                    sx={{
                        flex: 1,
                        overflowY: 'auto',
                        mb: 1,
                    }}
                >
                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.textDark.secondary}`,
                            borderRadius: '8px',
                            mb: 1.5,
                            px: 2,
                            py: 1.5,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Checkbox
                                checked={kycData?.isNameChecked ?? false}
                                onChange={() => handleCheckboxChange('isNameChecked')}
                                disabled={isSubmitBtnLoading || isCloseBtnLoading}
                                sx={{ p: 0 }}
                            />
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: `${theme.palette.primary[200]}0D`,
                                    border: `1px solid ${theme.palette.primary[200]}33`,
                                }}
                            >
                                {iconMap['person']}
                            </Box>
                            <Box>
                                <Typography variant='subtitle_500' sx={{ fontWeight: 600 }}>Name</Typography>
                                <Typography variant="subtitle_500" color="text.secondary">
                                    Current: {kycData?.name ?? '-'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.textDark.secondary}`,
                            borderRadius: '8px',
                            mb: 1.5,
                            px: 2,
                            py: 1.5,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Checkbox
                                checked={kycData?.isEmailChecked ?? false}
                                onChange={() => handleCheckboxChange('isEmailChecked')}
                                disabled={isSubmitBtnLoading || isCloseBtnLoading}
                                sx={{ p: 0 }}
                            />
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: `${theme.palette.primary[200]}0D`,
                                    border: `1px solid ${theme.palette.primary[200]}33`,
                                }}
                            >
                                {iconMap['email']}
                            </Box>
                            <Box>
                                <Typography variant='subtitle_500' sx={{ fontWeight: 600 }}>Email</Typography>
                                <Typography variant="subtitle_500" color="text.secondary">
                                    Current: {kycData?.emailId ?? '-'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.textDark.secondary}`,
                            borderRadius: '8px',
                            mb: 1.5,
                            px: 2,
                            py: 1.5,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Checkbox
                                checked={kycData?.isMobileChecked ?? false}
                                onChange={() => handleCheckboxChange('isMobileChecked')}
                                disabled={isSubmitBtnLoading || isCloseBtnLoading}
                                sx={{ p: 0 }}
                            />
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: `${theme.palette.primary[200]}0D`,
                                    border: `1px solid ${theme.palette.primary[200]}33`,
                                }}
                            >
                                {iconMap['phone']}
                            </Box>
                            <Box>
                                <Typography variant='subtitle_500' sx={{ fontWeight: 600 }}>Mobile Number</Typography>
                                <Typography variant="subtitle_500" color="text.secondary">
                                    Current: {kycData?.mobile ?? '-'}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            border: `1px solid ${theme.palette.textDark.secondary}`,
                            borderRadius: '8px',
                            mb: 1.5,
                            px: 2,
                            py: 1.5,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Checkbox
                                checked={kycData?.isAddressChecked ?? false}
                                onChange={() => handleCheckboxChange('isAddressChecked')}
                                disabled={isSubmitBtnLoading || isCloseBtnLoading}
                                sx={{ p: 0 }}
                            />
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: `${theme.palette.primary[200]}0D`,
                                    border: `1px solid ${theme.palette.primary[200]}33`,
                                }}
                            >
                                {iconMap['address']}
                            </Box>
                            <Box>
                                <Typography variant='subtitle_500' sx={{ fontWeight: 600 }}>Address</Typography>
                            </Box>
                        </Box>

                        {kycData?.addresses && (kycData?.addresses ?? []).length > 0 && (
                            <Box sx={{ ml: 7.5, mt: 1.5 }}>
                                {(kycData?.addresses ?? []).map((address: KycAddressDetails, addressIndex: number) => (
                                    <Box
                                        key={addressIndex}
                                        sx={{
                                            bgcolor: theme.palette.grey[50],
                                            borderRadius: '8px',
                                            p: 2,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="subtitle_500" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                                            Current Address {addressIndex + 1} Details:
                                        </Typography>
                                        <Grid container spacing={0.5}>
                                            {addressFieldConfig.map((field) => (
                                                <Grid item xs={12} key={field.key}>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Typography
                                                            variant="subtitle_500"
                                                            color="text.secondary"
                                                            sx={{ minWidth: 140 }}
                                                        >
                                                            {field.label}
                                                        </Typography>
                                                        <Typography variant="subtitle_500" color="text.secondary">
                                                            :
                                                        </Typography>
                                                        <Typography variant="subtitle_500">
                                                            {`${address[field.key] ?? '-'}`}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        bgcolor: `${theme.palette.primary.main}0A`,
                        border: `1px solid ${theme.palette.primary.main}20`,
                        borderRadius: '8px',
                        px: 2,
                        py: 1.5,
                        flexShrink: 0,
                    }}
                >
                    <InfoOutlinedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                    <Typography variant="body_500" color="primary.main">
                        Only selected fields will be updated.
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2.5, gap: 2 }}>
                <LoadingButton
                    variant="outlined"
                    fullWidth
                    onClick={onClose}
                    loading={isCloseBtnLoading}
                    loadingIndicator={
                        <CircularProgress size={18} sx={{ color: theme.palette.primary[500] }} />
                    }
                    disabled={isSubmitBtnLoading}
                    sx={{
                        borderRadius: '8px',
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 600,
                        color: 'text.primary',
                        borderColor: 'divider',
                    }}
                >
                    Close
                </LoadingButton>
                <LoadingButton
                    variant="contained"
                    fullWidth
                    onClick={onSubmit}
                    loading={isSubmitBtnLoading}
                    loadingIndicator={
                        <CircularProgress size={18} sx={{ color: theme.palette.primary[500] }} />
                    }
                    disabled={
                        isCloseBtnLoading ||
                        (!kycData?.isNameChecked && !kycData?.isEmailChecked && !kycData?.isMobileChecked && !kycData?.isAddressChecked)
                    }
                    sx={{
                        borderRadius: '8px',
                        py: 1.2,
                        textTransform: 'none',
                        fontWeight: 600,
                        color: 'common.white',
                    }}
                >
                    Submit
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}
